import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Briefcase,
  Hammer,
  FileText,
  Receipt,
  Plus,
  Send,
  Download,
  Eye,
  Loader2,
  Globe,
  Search,
  ShoppingCart,
  Layout,
  Share2,
  Palette,
  Home,
  Building,
  Bath,
  UtensilsCrossed,
  Paintbrush,
  TreePine,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Calculator,
  FileSignature,
  Scale,
  Calendar,
  CheckSquare,
  Printer
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Web & Marketing Contract Templates
const webContractTemplates = [
  {
    id: 'web_design_contract',
    name: 'Website Design & Development Contract',
    description: 'Complete contract for website design and development projects',
    icon: Globe,
    sections: [
      { name: 'Parties', description: 'Client and service provider information' },
      { name: 'Scope of Work', description: 'Detailed project deliverables' },
      { name: 'Timeline', description: 'Project milestones and deadlines' },
      { name: 'Payment Terms', description: 'Pricing and payment schedule' },
      { name: 'Revisions', description: 'Number of revisions included' },
      { name: 'Ownership', description: 'IP and ownership rights' },
      { name: 'Termination', description: 'Cancellation terms' },
      { name: 'Signatures', description: 'Both parties sign here' }
    ],
    defaultTerms: {
      revisions: 3,
      paymentSchedule: '50% upfront, 50% on completion',
      ownershipTransfer: 'Upon final payment',
      cancellationFee: '25% of total project cost',
      warrantyPeriod: '30 days'
    }
  },
  {
    id: 'seo_services_contract',
    name: 'SEO Services Agreement',
    description: 'Monthly SEO services retainer contract',
    icon: Search,
    sections: [
      { name: 'Parties', description: 'Client and agency information' },
      { name: 'Services', description: 'SEO services to be provided' },
      { name: 'Reporting', description: 'Monthly reporting requirements' },
      { name: 'Term & Renewal', description: 'Contract duration and renewal' },
      { name: 'Payment Terms', description: 'Monthly fee and payment terms' },
      { name: 'Performance', description: 'KPIs and expectations' },
      { name: 'Confidentiality', description: 'NDA and data protection' },
      { name: 'Termination', description: 'Notice period and terms' },
      { name: 'Signatures', description: 'Both parties sign here' }
    ],
    defaultTerms: {
      minimumTerm: '6 months',
      noticePeriod: '30 days',
      reportingFrequency: 'Monthly',
      paymentDue: 'Net 15',
      autoRenewal: true
    }
  },
  {
    id: 'marketing_retainer',
    name: 'Digital Marketing Retainer Agreement',
    description: 'Ongoing digital marketing services contract',
    icon: Share2,
    sections: [
      { name: 'Parties', description: 'Client and agency details' },
      { name: 'Services', description: 'Marketing services included' },
      { name: 'Hours & Scope', description: 'Monthly hours allocation' },
      { name: 'Deliverables', description: 'Expected outputs' },
      { name: 'Reporting', description: 'Performance reporting' },
      { name: 'Payment', description: 'Retainer fee and billing' },
      { name: 'Term', description: 'Contract period' },
      { name: 'Termination', description: 'Exit terms' },
      { name: 'Signatures', description: 'Authorization' }
    ],
    defaultTerms: {
      monthlyHours: 20,
      minimumTerm: '3 months',
      paymentDue: '1st of each month',
      rolloverHours: false,
      noticePeriod: '30 days'
    }
  }
];

// Web & Marketing Project Types
const webProjectTypes = [
  {
    id: 'website_design',
    name: 'Website Design',
    icon: Globe,
    color: 'blue',
    description: 'Full website design and development projects',
    fields: [
      { name: 'target_pages', label: 'Target Number of Pages', type: 'number', required: true },
      { name: 'responsive', label: 'Responsive Design Required', type: 'checkbox', required: false },
      { name: 'cms', label: 'CMS Platform', type: 'select', options: ['WordPress', 'Webflow', 'Custom', 'Shopify', 'Other'], required: true },
      { name: 'design_style', label: 'Design Style', type: 'select', options: ['Modern', 'Minimalist', 'Corporate', 'Creative', 'E-commerce'], required: false },
      { name: 'reference_sites', label: 'Reference Websites', type: 'textarea', required: false },
      { name: 'brand_guidelines', label: 'Brand Guidelines Available', type: 'checkbox', required: false },
      { name: 'hosting_needed', label: 'Hosting Required', type: 'checkbox', required: false },
      { name: 'ssl_certificate', label: 'SSL Certificate', type: 'checkbox', required: false }
    ]
  },
  {
    id: 'seo_campaign',
    name: 'SEO Campaign',
    icon: Search,
    color: 'green',
    description: 'Search engine optimization projects',
    fields: [
      { name: 'target_keywords', label: 'Target Keywords', type: 'textarea', required: true },
      { name: 'current_ranking', label: 'Current Search Ranking', type: 'text', required: false },
      { name: 'competitors', label: 'Main Competitors', type: 'textarea', required: false },
      { name: 'local_seo', label: 'Local SEO Focus', type: 'checkbox', required: false },
      { name: 'content_creation', label: 'Content Creation Included', type: 'checkbox', required: false }
    ]
  },
  {
    id: 'ecommerce',
    name: 'E-commerce Store',
    icon: ShoppingCart,
    color: 'purple',
    description: 'Online store setup and configuration',
    fields: [
      { name: 'product_count', label: 'Estimated Product Count', type: 'number', required: true },
      { name: 'platform', label: 'E-commerce Platform', type: 'select', options: ['Shopify', 'WooCommerce', 'Magento', 'BigCommerce', 'Custom'], required: true },
      { name: 'payment_gateways', label: 'Payment Gateways', type: 'text', required: true },
      { name: 'shipping_integration', label: 'Shipping Integration Required', type: 'checkbox', required: false },
      { name: 'inventory_management', label: 'Inventory Management', type: 'checkbox', required: false },
      { name: 'multi_currency', label: 'Multi-Currency Support', type: 'checkbox', required: false }
    ]
  },
  {
    id: 'landing_page',
    name: 'Landing Page',
    icon: Layout,
    color: 'orange',
    description: 'Single page marketing campaigns',
    fields: [
      { name: 'campaign_goal', label: 'Campaign Goal', type: 'select', options: ['Lead Generation', 'Product Launch', 'Event Registration', 'App Download', 'Other'], required: true },
      { name: 'a_b_testing', label: 'A/B Testing Required', type: 'checkbox', required: false },
      { name: 'form_fields', label: 'Form Fields Needed', type: 'textarea', required: false },
      { name: 'analytics_tracking', label: 'Analytics Tracking', type: 'checkbox', required: false }
    ]
  },
  {
    id: 'social_media',
    name: 'Social Media Management',
    icon: Share2,
    color: 'pink',
    description: 'Social media marketing and management',
    fields: [
      { name: 'platforms', label: 'Target Platforms', type: 'text', required: true },
      { name: 'posts_per_week', label: 'Posts Per Week', type: 'number', required: true },
      { name: 'content_calendar', label: 'Content Calendar Included', type: 'checkbox', required: false },
      { name: 'paid_ads', label: 'Paid Advertising', type: 'checkbox', required: false }
    ]
  },
  {
    id: 'branding',
    name: 'Branding & Identity',
    icon: Palette,
    color: 'indigo',
    description: 'Brand identity and visual design',
    fields: [
      { name: 'logo_design', label: 'Logo Design', type: 'checkbox', required: false },
      { name: 'color_palette', label: 'Color Palette', type: 'checkbox', required: false },
      { name: 'typography', label: 'Typography Selection', type: 'checkbox', required: false },
      { name: 'brand_guidelines', label: 'Brand Guidelines Document', type: 'checkbox', required: false }
    ]
  }
];

// Web Quote Templates
const webQuoteTemplates = [
  {
    id: 'standard_web',
    name: 'Standard Web Quote',
    description: 'General website development quote',
    sections: ['Design', 'Development', 'Content', 'Testing', 'Deployment'],
    defaultItems: [
      { description: 'UI/UX Design', quantity: 1, unit: 'project', rate: 2500 },
      { description: 'Frontend Development', quantity: 1, unit: 'project', rate: 3500 },
      { description: 'Backend Development', quantity: 1, unit: 'project', rate: 3000 },
      { description: 'Content Migration', quantity: 1, unit: 'project', rate: 500 },
      { description: 'QA Testing', quantity: 1, unit: 'project', rate: 750 }
    ]
  },
  {
    id: 'seo_service',
    name: 'SEO Service Quote',
    description: 'Monthly SEO retainer quote',
    sections: ['Audit', 'On-Page', 'Off-Page', 'Reporting'],
    defaultItems: [
      { description: 'Initial SEO Audit', quantity: 1, unit: 'project', rate: 1500 },
      { description: 'Keyword Research', quantity: 1, unit: 'project', rate: 800 },
      { description: 'On-Page Optimization', quantity: 1, unit: 'month', rate: 1200 },
      { description: 'Link Building', quantity: 1, unit: 'month', rate: 1500 },
      { description: 'Monthly Reporting', quantity: 1, unit: 'month', rate: 300 }
    ]
  },
  {
    id: 'retainer',
    name: 'Monthly Retainer Quote',
    description: 'Ongoing monthly services',
    sections: ['Maintenance', 'Updates', 'Support'],
    defaultItems: [
      { description: 'Website Maintenance', quantity: 1, unit: 'month', rate: 500 },
      { description: 'Security Updates', quantity: 1, unit: 'month', rate: 200 },
      { description: 'Content Updates (up to 5/month)', quantity: 1, unit: 'month', rate: 400 },
      { description: 'Priority Support', quantity: 1, unit: 'month', rate: 300 }
    ]
  }
];

// Construction Project Types
const constructionProjectTypes = [
  {
    id: 'home_renovation',
    name: 'Home Renovation',
    icon: Home,
    color: 'amber',
    description: 'Full home renovation and remodeling projects',
    fields: [
      { name: 'property_type', label: 'Property Type', type: 'select', options: ['Single Family', 'Townhouse', 'Condo', 'Multi-Family', 'Commercial'], required: true },
      { name: 'square_footage', label: 'Total Square Footage', type: 'number', required: true },
      { name: 'rooms_affected', label: 'Rooms to Renovate', type: 'textarea', required: true },
      { name: 'structural_changes', label: 'Structural Changes Required', type: 'checkbox', required: false },
      { name: 'permit_required', label: 'Permits Required', type: 'checkbox', required: false },
      { name: 'hvac_update', label: 'HVAC Updates', type: 'checkbox', required: false },
      { name: 'electrical_update', label: 'Electrical Updates', type: 'checkbox', required: false },
      { name: 'plumbing_update', label: 'Plumbing Updates', type: 'checkbox', required: false },
      { name: 'timeline_weeks', label: 'Estimated Timeline (weeks)', type: 'number', required: false },
      { name: 'occupancy_during', label: 'Client Living On-Site During Work', type: 'checkbox', required: false },
      { name: 'material_grade', label: 'Material Grade', type: 'select', options: ['Standard', 'Premium', 'Luxury'], required: false },
      { name: 'special_requirements', label: 'Special Requirements', type: 'textarea', required: false }
    ]
  },
  {
    id: 'new_construction',
    name: 'New Construction',
    icon: Building,
    color: 'blue',
    description: 'Ground-up construction projects',
    fields: [
      { name: 'building_type', label: 'Building Type', type: 'select', options: ['Residential', 'Commercial', 'Industrial', 'Mixed-Use'], required: true },
      { name: 'lot_size', label: 'Lot Size (sq ft)', type: 'number', required: true },
      { name: 'building_sqft', label: 'Building Square Footage', type: 'number', required: true },
      { name: 'stories', label: 'Number of Stories', type: 'number', required: true },
      { name: 'bedrooms', label: 'Bedrooms', type: 'number', required: false },
      { name: 'bathrooms', label: 'Bathrooms', type: 'number', required: false },
      { name: 'garage', label: 'Garage Type', type: 'select', options: ['None', 'Attached 1-Car', 'Attached 2-Car', 'Attached 3-Car', 'Detached'], required: false },
      { name: 'foundation_type', label: 'Foundation Type', type: 'select', options: ['Slab', 'Crawl Space', 'Basement', 'Pier & Beam'], required: true },
      { name: 'architectural_plans', label: 'Architectural Plans Provided', type: 'checkbox', required: false },
      { name: 'site_prep_needed', label: 'Site Preparation Required', type: 'checkbox', required: false },
      { name: 'utility_connections', label: 'Utility Connections Needed', type: 'checkbox', required: false }
    ]
  },
  {
    id: 'kitchen_remodel',
    name: 'Kitchen Remodel',
    icon: UtensilsCrossed,
    color: 'green',
    description: 'Kitchen renovation and upgrades',
    fields: [
      { name: 'kitchen_size', label: 'Kitchen Size (sq ft)', type: 'number', required: true },
      { name: 'layout_change', label: 'Layout Changes', type: 'checkbox', required: false },
      { name: 'cabinet_type', label: 'Cabinet Type', type: 'select', options: ['Stock', 'Semi-Custom', 'Custom', 'Refinish Existing'], required: true },
      { name: 'countertop_material', label: 'Countertop Material', type: 'select', options: ['Laminate', 'Granite', 'Quartz', 'Marble', 'Butcher Block', 'Concrete'], required: true },
      { name: 'appliances_included', label: 'Appliances Included', type: 'checkbox', required: false },
      { name: 'backsplash', label: 'Backsplash Required', type: 'checkbox', required: false },
      { name: 'flooring', label: 'New Flooring', type: 'checkbox', required: false },
      { name: 'lighting_upgrade', label: 'Lighting Upgrade', type: 'checkbox', required: false },
      { name: 'island', label: 'Kitchen Island', type: 'select', options: ['None', 'New Island', 'Modify Existing', 'Remove Existing'], required: false },
      { name: 'plumbing_relocation', label: 'Plumbing Relocation Needed', type: 'checkbox', required: false }
    ]
  },
  {
    id: 'bathroom_remodel',
    name: 'Bathroom Remodel',
    icon: Bath,
    color: 'cyan',
    description: 'Bathroom renovation projects',
    fields: [
      { name: 'bathroom_type', label: 'Bathroom Type', type: 'select', options: ['Full Bath', 'Half Bath', 'Master Bath', 'Guest Bath'], required: true },
      { name: 'bathroom_size', label: 'Bathroom Size (sq ft)', type: 'number', required: true },
      { name: 'shower_type', label: 'Shower Type', type: 'select', options: ['Tub/Shower Combo', 'Walk-in Shower', 'Freestanding Tub', 'Steam Shower', 'None'], required: false },
      { name: 'vanity_type', label: 'Vanity Type', type: 'select', options: ['Single Sink', 'Double Sink', 'Floating Vanity', 'Custom Built-in'], required: true },
      { name: 'tile_work', label: 'Tile Work Required', type: 'checkbox', required: false },
      { name: 'heated_floor', label: 'Heated Flooring', type: 'checkbox', required: false },
      { name: 'ventilation', label: 'Ventilation Upgrade', type: 'checkbox', required: false },
      { name: 'accessibility', label: 'ADA Accessibility Features', type: 'checkbox', required: false }
    ]
  },
  {
    id: 'roofing',
    name: 'Roofing',
    icon: Home,
    color: 'red',
    description: 'Roof repair and replacement',
    fields: [
      { name: 'job_type', label: 'Job Type', type: 'select', options: ['Full Replacement', 'Partial Repair', 'New Installation', 'Inspection Only'], required: true },
      { name: 'roof_size', label: 'Roof Size (sq ft)', type: 'number', required: true },
      { name: 'current_material', label: 'Current Material', type: 'select', options: ['Asphalt Shingle', 'Metal', 'Tile', 'Slate', 'Flat/TPO', 'Wood Shake'], required: false },
      { name: 'new_material', label: 'New Material', type: 'select', options: ['Asphalt Shingle', 'Metal', 'Tile', 'Slate', 'Flat/TPO', 'Wood Shake'], required: true },
      { name: 'layers_remove', label: 'Layers to Remove', type: 'number', required: false },
      { name: 'gutters', label: 'Gutter Work Included', type: 'checkbox', required: false },
      { name: 'skylights', label: 'Skylights', type: 'select', options: ['None', 'Keep Existing', 'Add New', 'Remove'], required: false },
      { name: 'ventilation', label: 'Ventilation Upgrade', type: 'checkbox', required: false }
    ]
  },
  {
    id: 'deck_patio',
    name: 'Deck & Patio',
    icon: TreePine,
    color: 'emerald',
    description: 'Outdoor living spaces',
    fields: [
      { name: 'project_type', label: 'Project Type', type: 'select', options: ['New Deck', 'Deck Replacement', 'Patio', 'Pergola', 'Screen Porch', 'Combination'], required: true },
      { name: 'size_sqft', label: 'Size (sq ft)', type: 'number', required: true },
      { name: 'material', label: 'Decking Material', type: 'select', options: ['Pressure Treated Wood', 'Cedar', 'Composite', 'PVC', 'Hardwood', 'Concrete/Pavers'], required: true },
      { name: 'levels', label: 'Number of Levels', type: 'number', required: false },
      { name: 'railings', label: 'Railing Type', type: 'select', options: ['Wood', 'Composite', 'Metal', 'Cable', 'Glass', 'None'], required: false },
      { name: 'stairs', label: 'Stairs Needed', type: 'checkbox', required: false },
      { name: 'lighting', label: 'Built-in Lighting', type: 'checkbox', required: false },
      { name: 'outdoor_kitchen', label: 'Outdoor Kitchen/Grill Area', type: 'checkbox', required: false }
    ]
  }
];

// Construction Quote Templates
const constructionQuoteTemplates = [
  {
    id: 'construction_estimate',
    name: 'Construction Estimate',
    description: 'Detailed construction project estimate',
    sections: ['Labor', 'Materials', 'Equipment', 'Permits & Fees', 'Contingency'],
    defaultItems: [
      { description: 'Demolition & Site Prep', category: 'Labor', quantity: 1, unit: 'project', rate: 2500 },
      { description: 'Framing Labor', category: 'Labor', quantity: 0, unit: 'sq ft', rate: 8 },
      { description: 'Electrical Rough-In', category: 'Labor', quantity: 1, unit: 'project', rate: 3500 },
      { description: 'Plumbing Rough-In', category: 'Labor', quantity: 1, unit: 'project', rate: 4000 },
      { description: 'Lumber & Framing Materials', category: 'Materials', quantity: 0, unit: 'lot', rate: 0 },
      { description: 'Drywall & Finishing', category: 'Materials', quantity: 0, unit: 'sheet', rate: 45 },
      { description: 'Equipment Rental', category: 'Equipment', quantity: 1, unit: 'week', rate: 500 },
      { description: 'Building Permits', category: 'Permits & Fees', quantity: 1, unit: 'project', rate: 0 },
      { description: 'Contingency (10%)', category: 'Contingency', quantity: 1, unit: 'project', rate: 0 }
    ]
  },
  {
    id: 'material_labor',
    name: 'Material & Labor Quote',
    description: 'Itemized materials and labor breakdown',
    sections: ['Materials', 'Labor', 'Subcontractors'],
    defaultItems: [
      { description: 'General Labor', category: 'Labor', quantity: 0, unit: 'hour', rate: 65 },
      { description: 'Skilled Trade Labor', category: 'Labor', quantity: 0, unit: 'hour', rate: 95 },
      { description: 'Project Manager', category: 'Labor', quantity: 0, unit: 'hour', rate: 85 },
      { description: 'Materials (itemized)', category: 'Materials', quantity: 1, unit: 'lot', rate: 0 },
      { description: 'Material Delivery', category: 'Materials', quantity: 1, unit: 'trip', rate: 150 },
      { description: 'Subcontractor - Electrical', category: 'Subcontractors', quantity: 1, unit: 'project', rate: 0 },
      { description: 'Subcontractor - Plumbing', category: 'Subcontractors', quantity: 1, unit: 'project', rate: 0 }
    ]
  },
  {
    id: 'change_order',
    name: 'Change Order Form',
    description: 'Document scope changes during project',
    sections: ['Change Description', 'Cost Impact', 'Schedule Impact'],
    defaultItems: [
      { description: 'Additional Labor', category: 'Labor', quantity: 0, unit: 'hour', rate: 75 },
      { description: 'Additional Materials', category: 'Materials', quantity: 1, unit: 'lot', rate: 0 },
      { description: 'Schedule Extension', category: 'Schedule', quantity: 0, unit: 'day', rate: 0 }
    ]
  }
];

const Forms = () => {
  const { user, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [businessType, setBusinessType] = useState(null);
  const [tenantName, setTenantName] = useState('');
  const [activeTab, setActiveTab] = useState('projects');
  const [expandedProject, setExpandedProject] = useState(null);
  const [clients, setClients] = useState([]);
  
  // Quote builder state
  const [isQuoteOpen, setIsQuoteOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [quoteItems, setQuoteItems] = useState([]);
  const [quoteClient, setQuoteClient] = useState('');
  const [quoteNotes, setQuoteNotes] = useState('');

  // Contract builder state
  const [isContractOpen, setIsContractOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [contractData, setContractData] = useState({
    clientName: '',
    clientEmail: '',
    clientAddress: '',
    clientPhone: '',
    projectName: '',
    projectDescription: '',
    startDate: '',
    endDate: '',
    totalAmount: '',
    paymentSchedule: '50% upfront, 50% on completion',
    revisions: 3,
    additionalTerms: '',
    services: []
  });

  useEffect(() => {
    const fetchTenantInfo = async () => {
      if (user?.organization_id) {
        try {
          const [tenantRes, clientsRes] = await Promise.all([
            axios.get(`${API_URL}/tenants/${user.organization_id}`, getAuthHeaders()),
            axios.get(`${API_URL}/clients`, getAuthHeaders())
          ]);
          setBusinessType(tenantRes.data.business_type || 'web_marketing');
          setTenantName(tenantRes.data.company_name || tenantRes.data.name || '');
          setClients(clientsRes.data || []);
        } catch (error) {
          console.error('Failed to fetch tenant info:', error);
          setBusinessType('web_marketing'); // Default
        }
      }
      setLoading(false);
    };
    fetchTenantInfo();
  }, [user, getAuthHeaders]);

  const projectTypes = businessType === 'construction' ? constructionProjectTypes : webProjectTypes;
  const quoteTemplates = businessType === 'construction' ? constructionQuoteTemplates : webQuoteTemplates;
  const themeColor = businessType === 'construction' ? 'amber' : 'blue';
  const ThemeIcon = businessType === 'construction' ? Hammer : Briefcase;

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-50 text-blue-600 border-blue-200',
      green: 'bg-green-50 text-green-600 border-green-200',
      purple: 'bg-purple-50 text-purple-600 border-purple-200',
      orange: 'bg-orange-50 text-orange-600 border-orange-200',
      pink: 'bg-pink-50 text-pink-600 border-pink-200',
      indigo: 'bg-indigo-50 text-indigo-600 border-indigo-200',
      amber: 'bg-amber-50 text-amber-600 border-amber-200',
      cyan: 'bg-cyan-50 text-cyan-600 border-cyan-200',
      red: 'bg-red-50 text-red-600 border-red-200',
      emerald: 'bg-emerald-50 text-emerald-600 border-emerald-200'
    };
    return colors[color] || colors.blue;
  };

  const getFieldTypeLabel = (type) => {
    const types = {
      text: 'Text',
      number: 'Number',
      textarea: 'Text Area',
      select: 'Dropdown',
      checkbox: 'Yes/No',
      date: 'Date'
    };
    return types[type] || type;
  };

  const openQuoteBuilder = (template) => {
    setSelectedTemplate(template);
    setQuoteItems(template.defaultItems.map(item => ({ ...item, quantity: item.quantity || 1 })));
    setQuoteClient('');
    setQuoteNotes('');
    setIsQuoteOpen(true);
  };

  const openContractBuilder = (contract) => {
    setSelectedContract(contract);
    setContractData({
      clientName: '',
      clientEmail: '',
      clientAddress: '',
      clientPhone: '',
      projectName: '',
      projectDescription: '',
      startDate: format(new Date(), 'yyyy-MM-dd'),
      endDate: '',
      totalAmount: '',
      paymentSchedule: contract.defaultTerms?.paymentSchedule || '50% upfront, 50% on completion',
      revisions: contract.defaultTerms?.revisions || 3,
      additionalTerms: '',
      services: []
    });
    setIsContractOpen(true);
  };

  const calculateTotal = () => {
    return quoteItems.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
  };

  const updateItemQuantity = (index, quantity) => {
    const updated = [...quoteItems];
    updated[index].quantity = parseFloat(quantity) || 0;
    setQuoteItems(updated);
  };

  const updateItemRate = (index, rate) => {
    const updated = [...quoteItems];
    updated[index].rate = parseFloat(rate) || 0;
    setQuoteItems(updated);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="forms-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl ${themeColor === 'construction' ? 'bg-amber-600' : 'bg-blue-600'} flex items-center justify-center`}>
            <ThemeIcon size={24} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {businessType === 'construction' ? 'Construction Forms' : 'Web & Marketing Forms'}
            </h1>
            <p className="text-slate-500">Project templates and quote builders for your business</p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className={`bg-white border border-slate-200 p-1`}>
          <TabsTrigger 
            value="projects" 
            className={`data-[state=active]:bg-${themeColor}-50 data-[state=active]:text-${themeColor}-700`}
          >
            <FileText size={16} className="mr-2" />
            Project Forms
          </TabsTrigger>
          <TabsTrigger 
            value="quotes" 
            className={`data-[state=active]:bg-${themeColor}-50 data-[state=active]:text-${themeColor}-700`}
          >
            <Receipt size={16} className="mr-2" />
            Quote Templates
          </TabsTrigger>
          {businessType === 'web_marketing' && (
            <TabsTrigger 
              value="contracts" 
              className={`data-[state=active]:bg-${themeColor}-50 data-[state=active]:text-${themeColor}-700`}
            >
              <FileSignature size={16} className="mr-2" />
              Contracts
            </TabsTrigger>
          )}
        </TabsList>

        {/* Project Forms Tab */}
        <TabsContent value="projects" className="space-y-4">
          <p className="text-sm text-slate-600">
            These forms are used when creating new projects. Each project type has specific fields for gathering requirements.
          </p>

          <div className="grid gap-4">
            {projectTypes.map((projectType) => {
              const Icon = projectType.icon;
              const isExpanded = expandedProject === projectType.id;
              
              return (
                <Card key={projectType.id} className="border-slate-200 shadow-sm">
                  <CardHeader 
                    className="cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => setExpandedProject(isExpanded ? null : projectType.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getColorClasses(projectType.color)}`}>
                          <Icon size={20} />
                        </div>
                        <div>
                          <CardTitle className="text-lg font-semibold text-slate-900">
                            {projectType.name}
                          </CardTitle>
                          <CardDescription>{projectType.description}</CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-slate-500">
                          {projectType.fields.length} fields
                        </span>
                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </div>
                    </div>
                  </CardHeader>
                  
                  {isExpanded && (
                    <CardContent className="border-t border-slate-100 pt-4">
                      <div className="space-y-2">
                        {projectType.fields.map((field, idx) => (
                          <div 
                            key={idx}
                            className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <span className="font-medium text-slate-900">{field.label}</span>
                              {field.required && (
                                <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">Required</span>
                              )}
                            </div>
                            <span className="text-xs bg-slate-200 text-slate-600 px-2 py-1 rounded">
                              {getFieldTypeLabel(field.type)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Quote Templates Tab */}
        <TabsContent value="quotes" className="space-y-4">
          <p className="text-sm text-slate-600">
            Create professional quotes for your clients using these templates.
          </p>

          <div className="grid gap-4">
            {quoteTemplates.map((template) => (
              <Card key={template.id} className="border-slate-200 shadow-sm">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                        <Receipt size={20} className="text-green-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg font-semibold text-slate-900">
                          {template.name}
                        </CardTitle>
                        <CardDescription>{template.description}</CardDescription>
                      </div>
                    </div>
                    <Button 
                      onClick={() => openQuoteBuilder(template)}
                      className={`${themeColor === 'amber' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                    >
                      <Calculator size={16} className="mr-2" />
                      Create Quote
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="border-t border-slate-100">
                  <div className="flex flex-wrap gap-2">
                    {template.sections.map((section, idx) => (
                      <span 
                        key={idx} 
                        className={`text-xs ${themeColor === 'amber' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'} px-2 py-1 rounded`}
                      >
                        {section}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Contracts Tab - Web & Marketing Only */}
        {businessType === 'web_marketing' && (
          <TabsContent value="contracts" className="space-y-4">
            <p className="text-sm text-slate-600">
              Professional service contracts for your web and marketing projects. Generate legally-formatted agreements ready for client signature.
            </p>

            <div className="grid gap-4">
              {webContractTemplates.map((contract) => {
                const Icon = contract.icon;
                return (
                  <Card key={contract.id} className="border-slate-200 shadow-sm">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-lg bg-indigo-50 flex items-center justify-center">
                            <Icon size={24} className="text-indigo-600" />
                          </div>
                          <div>
                            <CardTitle className="text-lg font-semibold text-slate-900">
                              {contract.name}
                            </CardTitle>
                            <CardDescription>{contract.description}</CardDescription>
                          </div>
                        </div>
                        <Button 
                          onClick={() => openContractBuilder(contract)}
                          className="bg-indigo-600 hover:bg-indigo-700"
                        >
                          <FileSignature size={16} className="mr-2" />
                          Create Contract
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="border-t border-slate-100">
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-sm font-semibold text-slate-700 mb-2">Contract Sections</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {contract.sections.map((section, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                                <CheckSquare size={14} className="text-indigo-500" />
                                {section.name}
                              </div>
                            ))}
                          </div>
                        </div>
                        {contract.defaultTerms && (
                          <div className="pt-3 border-t border-slate-100">
                            <h4 className="text-sm font-semibold text-slate-700 mb-2">Default Terms</h4>
                            <div className="flex flex-wrap gap-3">
                              {Object.entries(contract.defaultTerms).map(([key, value], idx) => (
                                <span key={idx} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}: {String(value)}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Contract Tips */}
            <Card className="border-indigo-200 shadow-sm bg-indigo-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Scale size={20} className="text-indigo-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-slate-900">Contract Best Practices</h4>
                    <ul className="text-sm text-slate-600 mt-2 space-y-1 list-disc list-inside">
                      <li>Always specify exact deliverables and timelines</li>
                      <li>Include a clear payment schedule with milestones</li>
                      <li>Define revision limits to prevent scope creep</li>
                      <li>Add intellectual property transfer terms</li>
                      <li>Include termination and cancellation clauses</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Quote Builder Dialog */}
      <Dialog open={isQuoteOpen} onOpenChange={setIsQuoteOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator size={20} />
              {selectedTemplate?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Client Selection */}
            <div className="space-y-2">
              <Label>Client</Label>
              <Select value={quoteClient} onValueChange={setQuoteClient}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Line Items */}
            <div className="space-y-3">
              <Label>Line Items</Label>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-3 font-medium text-slate-700">Description</th>
                      <th className="text-right p-3 font-medium text-slate-700 w-24">Qty</th>
                      <th className="text-right p-3 font-medium text-slate-700 w-20">Unit</th>
                      <th className="text-right p-3 font-medium text-slate-700 w-28">Rate</th>
                      <th className="text-right p-3 font-medium text-slate-700 w-28">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quoteItems.map((item, idx) => (
                      <tr key={idx} className="border-t border-slate-100">
                        <td className="p-3 text-slate-900">{item.description}</td>
                        <td className="p-3">
                          <Input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateItemQuantity(idx, e.target.value)}
                            className="w-full text-right h-8"
                          />
                        </td>
                        <td className="p-3 text-right text-slate-600">{item.unit}</td>
                        <td className="p-3">
                          <Input
                            type="number"
                            value={item.rate}
                            onChange={(e) => updateItemRate(idx, e.target.value)}
                            className="w-full text-right h-8"
                          />
                        </td>
                        <td className="p-3 text-right font-medium text-slate-900">
                          ${(item.quantity * item.rate).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                    <tr>
                      <td colSpan={4} className="p-3 text-right font-semibold text-slate-900">
                        Total:
                      </td>
                      <td className="p-3 text-right font-bold text-lg text-slate-900">
                        ${calculateTotal().toLocaleString()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes / Terms</Label>
              <Textarea
                value={quoteNotes}
                onChange={(e) => setQuoteNotes(e.target.value)}
                placeholder="Add any notes, terms, or conditions..."
                rows={3}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsQuoteOpen(false)}>
                Cancel
              </Button>
              <Button variant="outline">
                <Download size={16} className="mr-2" />
                Download PDF
              </Button>
              <Button className={`${themeColor === 'amber' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
                <Send size={16} className="mr-2" />
                Send to Client
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contract Builder Dialog */}
      <Dialog open={isContractOpen} onOpenChange={setIsContractOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature size={20} className="text-indigo-600" />
              {selectedContract?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Client Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold">1</span>
                Client Information
              </h3>
              <div className="grid grid-cols-2 gap-4 pl-8">
                <div className="space-y-2">
                  <Label>Client Name / Company *</Label>
                  <Input
                    value={contractData.clientName}
                    onChange={(e) => setContractData({ ...contractData, clientName: e.target.value })}
                    placeholder="Client or company name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={contractData.clientEmail}
                    onChange={(e) => setContractData({ ...contractData, clientEmail: e.target.value })}
                    placeholder="client@email.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={contractData.clientPhone}
                    onChange={(e) => setContractData({ ...contractData, clientPhone: e.target.value })}
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input
                    value={contractData.clientAddress}
                    onChange={(e) => setContractData({ ...contractData, clientAddress: e.target.value })}
                    placeholder="123 Main St, City, State"
                  />
                </div>
              </div>
            </div>

            {/* Project Details */}
            <div className="space-y-4 border-t border-slate-100 pt-6">
              <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold">2</span>
                Project / Service Details
              </h3>
              <div className="pl-8 space-y-4">
                <div className="space-y-2">
                  <Label>Project Name *</Label>
                  <Input
                    value={contractData.projectName}
                    onChange={(e) => setContractData({ ...contractData, projectName: e.target.value })}
                    placeholder="e.g., Website Redesign for ABC Corp"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Scope of Work / Description *</Label>
                  <Textarea
                    value={contractData.projectDescription}
                    onChange={(e) => setContractData({ ...contractData, projectDescription: e.target.value })}
                    placeholder="Describe the services to be provided, deliverables, and any specific requirements..."
                    rows={4}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={contractData.startDate}
                      onChange={(e) => setContractData({ ...contractData, startDate: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date / Deadline</Label>
                    <Input
                      type="date"
                      value={contractData.endDate}
                      onChange={(e) => setContractData({ ...contractData, endDate: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Terms */}
            <div className="space-y-4 border-t border-slate-100 pt-6">
              <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold">3</span>
                Payment Terms
              </h3>
              <div className="pl-8 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Total Amount ($) *</Label>
                    <Input
                      type="number"
                      value={contractData.totalAmount}
                      onChange={(e) => setContractData({ ...contractData, totalAmount: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Payment Schedule</Label>
                    <Select
                      value={contractData.paymentSchedule}
                      onValueChange={(value) => setContractData({ ...contractData, paymentSchedule: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="100% upfront">100% Upfront</SelectItem>
                        <SelectItem value="50% upfront, 50% on completion">50% Upfront, 50% on Completion</SelectItem>
                        <SelectItem value="33% upfront, 33% midpoint, 34% completion">33% / 33% / 34% (Three Payments)</SelectItem>
                        <SelectItem value="Monthly retainer">Monthly Retainer</SelectItem>
                        <SelectItem value="Net 15">Net 15 (Due in 15 days)</SelectItem>
                        <SelectItem value="Net 30">Net 30 (Due in 30 days)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {selectedContract?.id === 'web_design_contract' && (
                  <div className="space-y-2">
                    <Label>Number of Revisions Included</Label>
                    <Select
                      value={String(contractData.revisions)}
                      onValueChange={(value) => setContractData({ ...contractData, revisions: parseInt(value) })}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 Revision</SelectItem>
                        <SelectItem value="2">2 Revisions</SelectItem>
                        <SelectItem value="3">3 Revisions</SelectItem>
                        <SelectItem value="5">5 Revisions</SelectItem>
                        <SelectItem value="0">Unlimited</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Terms */}
            <div className="space-y-4 border-t border-slate-100 pt-6">
              <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold">4</span>
                Additional Terms & Conditions
              </h3>
              <div className="pl-8">
                <Textarea
                  value={contractData.additionalTerms}
                  onChange={(e) => setContractData({ ...contractData, additionalTerms: e.target.value })}
                  placeholder="Add any custom terms, special conditions, or notes..."
                  rows={3}
                />
              </div>
            </div>

            {/* Contract Preview Note */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Eye size={18} className="text-slate-500 mt-0.5" />
                <div className="text-sm text-slate-600">
                  <p className="font-medium text-slate-900">Contract Preview</p>
                  <p className="mt-1">
                    A professional contract document will be generated with your business details, 
                    standard legal clauses, signature fields, and the information provided above.
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsContractOpen(false)}>
                Cancel
              </Button>
              <Button variant="outline">
                <Eye size={16} className="mr-2" />
                Preview
              </Button>
              <Button variant="outline">
                <Download size={16} className="mr-2" />
                Download PDF
              </Button>
              <Button className="bg-indigo-600 hover:bg-indigo-700">
                <Send size={16} className="mr-2" />
                Send to Client for Signature
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Forms;
